'use client';

import { useState, useEffect } from 'react';

export interface Meeting {
  id: string;
  name: string;
  date: string;
  duration: string;
  participants: number;
  status: 'completed' | 'ongoing' | 'scheduled';
  createdAt: string;
  lastAccessed?: string;
}

const STORAGE_KEY = 'evolutto-meeting-history';

export function useMeetingHistory() {
  const [meetings, setMeetings] = useState<Meeting[]>([]);

  // Carregar histórico do localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsedMeetings = JSON.parse(stored);
          setMeetings(parsedMeetings);
        } else {
          // Inicializar com array vazio se não há dados salvos
          setMeetings([]);
        }
      } catch (error) {
        console.error('Erro ao carregar histórico de reuniões:', error);
        setMeetings([]);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Salvar histórico no localStorage
  const saveToStorage = (meetingsList: Meeting[]) => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(meetingsList));
      } catch (error) {
        console.error('Erro ao salvar histórico de reuniões:', error);
      }
    }
  };

  // Adicionar nova reunião
  const addMeeting = (meeting: Omit<Meeting, 'id' | 'createdAt'>) => {
    const newMeeting: Meeting = {
      ...meeting,
      id: Date.now().toString(),
      createdAt: new Date().toISOString(),
    };

    const updatedMeetings = [newMeeting, ...meetings];
    setMeetings(updatedMeetings);
    saveToStorage(updatedMeetings);

    return newMeeting;
  };

  // Atualizar reunião existente
  const updateMeeting = (id: string, updates: Partial<Meeting>) => {
    const updatedMeetings = meetings.map((meeting) =>
      meeting.id === id ? { ...meeting, ...updates } : meeting,
    );
    setMeetings(updatedMeetings);
    saveToStorage(updatedMeetings);
  };

  // Remover reunião
  const deleteMeeting = (id: string) => {
    const updatedMeetings = meetings.filter((meeting) => meeting.id !== id);
    setMeetings(updatedMeetings);
    saveToStorage(updatedMeetings);
  };

  // Marcar reunião como acessada
  const markAsAccessed = (id: string) => {
    updateMeeting(id, {
      lastAccessed: new Date().toISOString(),
    });
  };

  // Criar reunião a partir do nome
  const createMeetingFromName = (roomName: string) => {
    const now = new Date();
    const meeting = addMeeting({
      name: roomName,
      date: now.toISOString().split('T')[0],
      duration: '0min',
      participants: 1,
      status: 'ongoing',
    });

    return meeting;
  };

  // Finalizar reunião
  const completeMeeting = (id: string, duration: string, participants: number) => {
    updateMeeting(id, {
      status: 'completed',
      duration,
      participants,
    });
  };

  // Limpar todo o histórico
  const clearAllMeetings = () => {
    setMeetings([]);
    saveToStorage([]);
  };

  return {
    meetings,
    addMeeting,
    updateMeeting,
    deleteMeeting,
    markAsAccessed,
    createMeetingFromName,
    completeMeeting,
    clearAllMeetings,
  };
}
